/**
 * Future direction, alluded to and visibly penciled in.
 *
 * This section deliberately relaxes the old page's only-shipped-capabilities
 * rule (FR-001), and does it with guardrails: every row is direction already
 * on record — ideation over the linked vault, opt-in encrypted backup — and
 * nothing gets a date or a price. The dashed border and open-circle markers
 * are structure as information: not-yet, drawn as not-yet.
 *
 * The paid-cleanup teaser that used to lead this list shipped in 61e9 and
 * graduated to the feature cards ("Your ramble, rewritten") — a shipped,
 * priced feature cannot sit under "Penciled in" without the page arguing
 * with itself. What stays here must never promise local-only processing:
 * the rule line says out loud that some of this outgrows the phone.
 *
 * The closing rule repeats the privacy section's one network promise on
 * purpose, so a reader who arrives here skeptical leaves with the same
 * sentence they were given there.
 */

import { StyleSheet, Text as RawText, View } from "react-native";
import { Text } from "react-native-paper";
import { MarketingSection } from "@/components/marketing/section";
import { SectionHead } from "@/components/marketing/section-head";
import { useAppTheme } from "@/theme/paper-theme";
import { radius, spacing } from "@/theme/tokens";
import { PANEL_MEASURE } from "@/theme/web-tokens";

interface Teaser {
  lead: string;
  body: string;
}

const TEASERS: readonly Teaser[] = [
  {
    lead: "Ideas offered back.",
    body: " Sparkie notices what a week of scattered notes adds up to, and says so. The essay thesis was sitting across Tuesday's lecture and Thursday's reading. You just hadn't seen it yet.",
  },
  {
    lead: "A safe copy elsewhere.",
    body: " An encrypted backup you switch on yourself, for the day your phone goes swimming.",
  },
] as const;

const RULE =
  "Some of this needs bigger models than a phone can hold. Whatever does will be paid, clearly marked, and off until you turn it on. Same rule as everything else here.";

export function Later() {
  const theme = useAppTheme();

  return (
    <MarketingSection style={styles.section}>
      <SectionHead eyebrow="Later" heading="Penciled in." />

      <View style={[styles.panel, { borderColor: theme.colors.outline }]}>
        {TEASERS.map(({ lead, body }) => (
          <View key={lead} style={styles.row}>
            <RawText
              aria-hidden
              style={[styles.marker, { color: theme.colors.tertiary }]}
            >
              ○
            </RawText>
            <Text
              variant="bodyMedium"
              style={[styles.rowText, { color: theme.colors.onSurfaceVariant }]}
            >
              <Text
                variant="bodyMedium"
                style={[styles.rowLead, { color: theme.colors.onSurface }]}
              >
                {lead}
              </Text>
              {body}
            </Text>
          </View>
        ))}

        <Text
          variant="bodyMedium"
          style={[
            styles.rule,
            {
              color: theme.colors.onSurface,
              borderTopColor: theme.colors.outlineVariant,
            },
          ]}
        >
          {RULE}
        </Text>
      </View>
    </MarketingSection>
  );
}

/** The roadmap panel's rounding — softer than any token step on purpose. */
const PANEL_RADIUS = radius.sm + 8;

const styles = StyleSheet.create({
  section: {
    gap: spacing.xl,
  },
  panel: {
    alignSelf: "center",
    maxWidth: PANEL_MEASURE,
    width: "100%",
    borderWidth: 1,
    borderStyle: "dashed",
    borderRadius: PANEL_RADIUS,
    paddingVertical: spacing.lg + 4,
    paddingHorizontal: spacing.xl - 2,
    gap: spacing.md + 2,
  },
  row: {
    flexDirection: "row",
    gap: spacing.sm + 2,
    alignItems: "flex-start",
  },
  marker: {
    fontSize: 13,
    lineHeight: 20,
  },
  rowText: {
    flex: 1,
  },
  rowLead: {
    fontWeight: "600",
  },
  rule: {
    fontWeight: "500",
    borderTopWidth: 1,
    paddingTop: spacing.md,
  },
});
